"use client"

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { ComponentType } from 'react'

export type CommandPaletteMode = 'search' | 'task-picker'

export type TaskShortcutAction =
  | 'start-task-timer'
  | 'start-pomodoro-timer'
  | 'mark-task-complete'
  | 'schedule-task'

export interface CommandItem {
  id: string
  label: string
  icon?: ComponentType<{ className?: string }>
  shortcut?: string[]
  group: string
  onSelect: () => void
  keywords?: string[]
}

interface CommandPaletteState {
  isOpen: boolean
  mode: CommandPaletteMode
  pendingTaskAction: TaskShortcutAction | null
  commands: CommandItem[]
  setOpen: (open: boolean) => void
  toggle: () => void
  setMode: (mode: CommandPaletteMode) => void
  openTaskActionPicker: (action: TaskShortcutAction) => void
  clearTaskAction: () => void
  registerCommands: (commands: CommandItem[]) => void
  unregisterCommands: (ids: string[]) => void
}

export const useCommandPaletteStore = create<CommandPaletteState>()(
  devtools(
    (set, get) => ({
      isOpen: false,
      mode: 'search',
      pendingTaskAction: null,
      commands: [],

      setOpen: (open) => set({
        isOpen: open,
        // Reset mode when closing
        ...(open ? {} : { mode: 'search', pendingTaskAction: null }),
      }),

      toggle: () => {
        const { isOpen } = get()
        set({
          isOpen: !isOpen,
          // Reset mode when closing
          ...(!isOpen ? {} : { mode: 'search', pendingTaskAction: null }),
        })
      },

      setMode: (mode) => set({ mode }),

      openTaskActionPicker: (action) => set({
        isOpen: true,
        mode: 'task-picker',
        pendingTaskAction: action,
      }),

      clearTaskAction: () => set({
        pendingTaskAction: null,
        mode: 'search',
      }),

      registerCommands: (newCommands) => {
        const { commands } = get()
        // Avoid duplicates by id
        const existingIds = new Set(commands.map(c => c.id))
        const toAdd = newCommands.filter(c => !existingIds.has(c.id))
        if (toAdd.length > 0) {
          set({ commands: [...commands, ...toAdd] })
        }
      },

      unregisterCommands: (ids) => {
        const idsSet = new Set(ids)
        set({ commands: get().commands.filter(c => !idsSet.has(c.id)) })
      },
    }),
    { name: 'command-palette-store' }
  )
)
